import React, { useState } from 'react';
import { Smartphone, X, Apple, Bell, Download, MonitorSmartphone, CheckCircle2, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../lib/api';
import useInstallPrompt from '../hooks/useInstallPrompt';

// Optional env-driven store links. If not set, we treat as "Coming soon".
const PLAY_URL = process.env.REACT_APP_PLAY_STORE_URL || '';
const APP_STORE_URL = process.env.REACT_APP_APP_STORE_URL || '';

export function DownloadAppButton({ variant = 'light', testid = 'download-app-btn' }) {
  const [open, setOpen] = useState(false);

  const light = variant === 'light';
  const cls = light
    ? 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold text-raspberry-800 bg-white/80 backdrop-blur-md border border-raspberry-200 hover:bg-white transition-colors shadow-sm'
    : 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold text-white bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 transition-colors';

  return (
    <>
      <button onClick={() => setOpen(true)} className={cls} data-testid={testid}>
        <Smartphone className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Download App</span>
        <span className="sm:hidden">App</span>
      </button>
      {open && <DownloadAppModal onClose={() => setOpen(false)} />}
    </>
  );
}

function DownloadAppModal({ onClose }) {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { canInstall, isStandalone, isIOS, install } = useInstallPrompt();

  const handleInstall = async () => {
    const res = await install();
    if (res?.outcome === 'accepted') {
      toast.success('Gynovate AI/SI installed! Open it from your home screen.');
      onClose();
    } else if (res?.outcome === 'unavailable') {
      toast.info('Install option not available in this browser. Try Chrome on Android or Edge on desktop.');
    }
  };

  const handleNotify = async (e) => {
    e?.preventDefault?.();
    const v = email.trim();
    if (!v || !v.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setSubmitting(true);
    try {
      await fetch(`${API}/api/notify-me`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: v, channel: 'mobile-app-launch' }),
      }).catch(() => {});
      toast.success("Thanks! We'll email you the moment the app drops.");
      onClose();
    } catch (err) {
      toast.success("Thanks! We'll email you the moment the app drops.");
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="download-app-modal"
    >
      <div
        className="relative w-full max-w-md rounded-3xl overflow-hidden shadow-2xl max-h-[92vh] overflow-y-auto"
        style={{ background: 'linear-gradient(160deg, #ffffff 0%, #FFF5F6 60%, #FFFCF8 100%)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/80 backdrop-blur flex items-center justify-center text-slate-600 hover:text-slate-900 z-10"
          data-testid="download-app-modal-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-7 text-center">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-raspberry-800 text-white text-[10px] font-black tracking-[0.18em] uppercase mb-4">
            <Smartphone className="w-3 h-3" /> Install Gynovate AI/SI
          </div>
          <h3 className="text-2xl font-extrabold font-heading text-slate-900 leading-tight mb-2 tracking-tight">
            Install in one tap. Stay signed in forever.
          </h3>
          <p className="text-sm text-slate-600 mb-6 leading-relaxed max-w-xs mx-auto">
            Add Gynovate AI/SI to your phone or laptop. Opens in its own window, no login screen each time, works even on weak hospital Wi-Fi.
          </p>

          {/* ====== INSTALL AS PWA — PRIMARY ACTION ====== */}
          {isStandalone ? (
            <div className="rounded-2xl p-4 bg-emerald-50 border border-emerald-200 text-left mb-5" data-testid="pwa-installed-state">
              <div className="flex items-center gap-2 text-emerald-700 font-bold text-sm mb-1">
                <CheckCircle2 className="w-4 h-4" /> Already installed
              </div>
              <p className="text-xs text-slate-600">
                You&apos;re using Gynovate AI/SI as an installed app. You&apos;ll stay signed in — just open it from your home screen / dock next time.
              </p>
            </div>
          ) : isIOS ? (
            <div className="rounded-2xl p-4 bg-white border border-raspberry-100 text-left mb-5" data-testid="pwa-ios-instructions">
              <div className="flex items-center gap-2 text-raspberry-800 font-bold text-sm mb-2">
                <Apple className="w-4 h-4" /> Install on iPhone / iPad
              </div>
              <ol className="text-xs text-slate-700 leading-relaxed space-y-1.5 list-decimal pl-4">
                <li>Tap the <Share2 className="inline w-3.5 h-3.5 align-text-bottom mx-0.5" /> <b>Share</b> button at the bottom of Safari.</li>
                <li>Scroll down and tap <b>&ldquo;Add to Home Screen&rdquo;</b>.</li>
                <li>Tap <b>Add</b> — Gynovate AI/SI will appear as an icon on your home screen.</li>
              </ol>
            </div>
          ) : canInstall ? (
            <button
              onClick={handleInstall}
              className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-2xl bg-gradient-to-br from-raspberry-800 to-[#7a0e2e] text-white text-base font-bold hover:opacity-95 transition-opacity shadow-xl shadow-raspberry-200 mb-5"
              data-testid="pwa-install-btn"
            >
              <Download className="w-5 h-5" />
              Install Gynovate AI/SI — One Tap
            </button>
          ) : (
            <div className="rounded-2xl p-4 bg-white border border-raspberry-100 text-left mb-5" data-testid="pwa-manual-instructions">
              <div className="flex items-center gap-2 text-raspberry-800 font-bold text-sm mb-2">
                <MonitorSmartphone className="w-4 h-4" /> Install from your browser
              </div>
              <p className="text-xs text-slate-700 leading-relaxed">
                <b>Chrome / Edge (Android &amp; desktop):</b> Open the browser menu (⋮) → tap <b>&ldquo;Install Gynovate AI/SI&rdquo;</b> or <b>&ldquo;Add to Home Screen&rdquo;</b>.<br />
                <b>Safari (Mac):</b> File menu → <b>Add to Dock</b>.<br />
                Once installed, you stay signed in and the app opens in its own window.
              </p>
            </div>
          )}

          {/* ====== STORE LINKS (if env URLs set) OR Coming-Soon Notify ====== */}
          {(PLAY_URL || APP_STORE_URL) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
              {PLAY_URL && (
                <a
                  href={PLAY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold transition-colors shadow-md"
                  data-testid="download-app-play-btn"
                >
                  <PlayIcon /> <span className="text-left leading-tight">Get it on<br/><b className="text-base">Google Play</b></span>
                </a>
              )}
              {APP_STORE_URL && (
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold transition-colors shadow-md"
                  data-testid="download-app-ios-btn"
                >
                  <Apple className="w-5 h-5" /> <span className="text-left leading-tight">Download on<br/><b className="text-base">App Store</b></span>
                </a>
              )}
            </div>
          )}

          {/* Notify-me — only show when no Play Store URL configured */}
          {!PLAY_URL && (
            <form onSubmit={handleNotify} className="mt-5 p-4 rounded-2xl bg-white/70 backdrop-blur border border-raspberry-100">
              <p className="text-xs font-bold text-raspberry-800 tracking-wider uppercase mb-2 flex items-center justify-center gap-1.5">
                <Bell className="w-3.5 h-3.5" /> Native App Coming · Be First
              </p>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="flex-1 px-3 py-2 rounded-xl border border-slate-200 focus:border-raspberry-400 focus:outline-none text-sm bg-white"
                  data-testid="download-app-notify-email"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 rounded-xl bg-raspberry-800 text-white text-sm font-bold hover:bg-raspberry-700 transition-colors disabled:opacity-60"
                  data-testid="download-app-notify-submit"
                >
                  {submitting ? '…' : 'Notify Me'}
                </button>
              </div>
            </form>
          )}

          <p className="mt-5 text-[11px] text-slate-500">
            Tip: Once installed, you stay signed in across sessions — no re-login required.
          </p>
        </div>
      </div>
    </div>
  );
}

function PlayIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M3.6 1.5C3.2 1.7 3 2.1 3 2.6v18.8c0 .5.2.9.6 1.1l11-9.8L3.6 1.5zM14.5 12.5l3.4-3-13-7.3 9.6 10.3zm-9.6 10.3l13-7.3-3.4-3-9.6 10.3zm15.7-8.9l-2.5-1.4-3 3 3 3 2.5-1.4c.7-.4 1.1-1.1 1.1-1.6 0-.6-.4-1.2-1.1-1.6z"/>
    </svg>
  );
}

export default DownloadAppButton;
